import { useEffect, useState } from 'react';

const getBaseUrl = (): string | null => {
    const env = import.meta?.env?.VITE_API_URL || null;
    if (!env) return null;
    // remove trailing slash
    return env.replace(/\/$/, '');
};

/** Resolve an API path against VITE_API_URL (absolute URLs pass through). */
export const resolveApiUrl = (url: string): string => {
    const base = getBaseUrl();
    const isAbsolute = /^https?:\/\//i.test(url);
    return isAbsolute ? url : (base ? `${base}${url.startsWith('/') ? '' : '/'}${url}` : url);
};

export const getAuthToken = (): string | null => sessionStorage.getItem('token');

/** Shared 401 handling: drop the token and send the user to login. */
export const handleUnauthorized = (): void => {
    sessionStorage.removeItem('token');
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

export const fetcher = async (url: string, options?: RequestInit) => {
    const finalUrl = resolveApiUrl(url);

    // T026: Automatically include Authorization header if token exists
    const token = getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(options?.headers || {})
    };

    const response = await fetch(finalUrl, {
        ...options,
        headers
    });
    
    // T026: Handle 401 Unauthorized (token expired or invalid)
    if (response.status === 401) {
        // Clear token and redirect to login
        sessionStorage.removeItem('token');
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
        throw new Error('Authentication required');
    }
    
    // T026: Handle 403 Forbidden (insufficient permissions)
    if (response.status === 403) {
        const errorData = await response.json().catch(() => ({ detail: 'Insufficient permissions' }));
        throw new Error(errorData.detail || 'Insufficient permissions');
    }
    
    if (!response.ok) {
        // Try to get detailed error message from response body
        let errorMessage = `Error: ${response.statusText}`;
        try {
            const errorData = await response.json();
            if (errorData.detail) {
                errorMessage = `Error: ${errorData.detail}`;
            }
        } catch {
            // If response is not JSON, use statusText
        }
        throw new Error(errorMessage);
    }
    
    // Handle 204 No Content responses (e.g., from DELETE requests)
    if (response.status === 204) {
        return true;
    }
    
    return response.json();
};

export const useFetch = (url: string) => {
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await fetcher(url);
                setData(result);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'An error occurred';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [url]);

    return { data, error, loading };
};